import { useCallback, useEffect, useRef, useState } from 'react';
import { createBarcodeCameraSession } from '@/lib/barcodeCameraSession';

export const useBarcodeCamera = (enabled, onResult) => {
  const hostRef = useRef(null);
  const sessionRef = useRef(null);
  const resultRef = useRef(onResult);
  const deviceRef = useRef('');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(!document.hidden);
  resultRef.current = onResult;
  const stop = useCallback(() => { sessionRef.current?.stop(); sessionRef.current = null; }, []);
  const start = useCallback(() => {
    stop();
    if (!hostRef.current) return;
    setError('');
    const session = createBarcodeCameraSession({ host: hostRef.current, deviceId: deviceRef.current,
      onStatus: setStatus, onDevices: setDevices,
      onResult: (text, format) => resultRef.current(text, format),
      onError: message => { setStatus('error'); setError(message); },
    });
    sessionRef.current = session;
    void session.start();
  }, [stop]);
  useEffect(() => {
    if (enabled && visible) start();
    else { stop(); setStatus(enabled ? 'paused' : 'idle'); }
    return stop;
  }, [enabled, visible, start, stop]);
  useEffect(() => {
    const visibility = () => { if (document.hidden) stop(); setVisible(!document.hidden); };
    const hide = () => { stop(); setVisible(false); };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', visibility);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', visibility);
    };
  }, [stop]);
  const selectDevice = useCallback(id => { deviceRef.current = id; setDeviceId(id); start(); }, [start]);
  return { hostRef, status, error, devices, deviceId, start, stop, selectDevice };
};